import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { whatsappApi } from "./axios";
import { ServiceResponseI } from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

// Interfaces específicas para WhatsApp
export interface WhatsappQrStatusI {
  exists: boolean;
  path: string;
  timestamp?: number;
}

export interface WhatsappServiceStatusI {
  online: boolean;
  sessionStored: boolean;
  initialized: boolean;
  qrGenerated: boolean;
}

export interface WhatsappIsOnlineI {
  isOnline: boolean;
}

export interface WhatsappDebugFilesI {
  pathBaseDirectory: string;
  publicPath: string;
  wqrPath: string;
  publicExists: boolean;
  wqrExists: boolean;
  publicFiles: string[];
  wqrFiles: string[];
}

export interface SendMessageParamsI {
  number: string;
  message: string;
}

// Función para obtener estado del QR
const getWhatsappQrStatus = async (): Promise<
  ServiceResponseI<WhatsappQrStatusI>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await whatsappApi.get<ServiceResponseI<WhatsappQrStatusI>>(
      "/qr-status",
      {
        headers: {
          Authorization: validateToken,
          "Cache-Control": "no-cache",
        },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((err as Error).message);
  }
};

// Función para obtener estado completo del servicio
const getWhatsappServiceStatus = async (): Promise<
  ServiceResponseI<WhatsappServiceStatusI>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await whatsappApi.get<
      ServiceResponseI<WhatsappServiceStatusI>
    >("/service-status", {
      headers: {
        Authorization: validateToken,
        "Cache-Control": "no-cache",
      },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((err as Error).message);
  }
};

// Función para verificar si está online
const getWhatsappIsOnline = async (): Promise<
  ServiceResponseI<WhatsappIsOnlineI>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await whatsappApi.get<ServiceResponseI<WhatsappIsOnlineI>>(
      "/is-online",
      {
        headers: {
          Authorization: validateToken,
          "Cache-Control": "no-cache",
        },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((err as Error).message);
  }
};

// Función para debug de archivos
const getWhatsappDebugFiles = async (): Promise<
  ServiceResponseI<WhatsappDebugFilesI>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await whatsappApi.get<
      ServiceResponseI<WhatsappDebugFilesI>
    >("/debug-files", { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((err as Error).message);
  }
};

// Función para renovar servicio
const renewWhatsappService = async (): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await whatsappApi.post<ServiceResponseI<string>>(
      "/renew",
      {},
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// Función para forzar nueva sesión
const forceNewWhatsappSession = async (): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await whatsappApi.post<ServiceResponseI<string>>(
      "/force-new-session",
      {},
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// Función para reiniciar servicio
const restartWhatsappService = async (): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await whatsappApi.post<ServiceResponseI<string>>(
      "/restart",
      {},
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// Función para enviar mensaje
const sendWhatsappMessage = async (
  params: SendMessageParamsI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await whatsappApi.post<ServiceResponseI<string>>(
      "/send-message",
      params,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// Hook principal de WhatsApp
export const useWhatsapp = () => {
  const queryClient = useQueryClient();

  // Query para estado del QR
  const qrStatusQuery = useQuery(["whatsapp-qr-status"], getWhatsappQrStatus, {
    refetchOnWindowFocus: false,
    refetchInterval: 10000, // Refetch cada 10 segundos
    retry: 2,
  });

  // Query para estado del servicio
  const serviceStatusQuery = useQuery(
    ["whatsapp-service-status"],
    getWhatsappServiceStatus,
    {
      refetchOnWindowFocus: false,
      refetchInterval: 15000, // Refetch cada 15 segundos
      retry: 2,
    },
  );

  // Query para verificar si está online
  const isOnlineQuery = useQuery(["whatsapp-is-online"], getWhatsappIsOnline, {
    refetchOnWindowFocus: false,
    refetchInterval: 20000, // Refetch cada 20 segundos
    retry: 2,
  });

  // Query para debug de archivos (solo cuando se llama manualmente)
  const debugFilesQuery = useQuery(
    ["whatsapp-debug-files"],
    getWhatsappDebugFiles,
    {
      enabled: false, // Solo se ejecuta manualmente
      refetchOnWindowFocus: false,
    },
  );

  // Mutation para renovar servicio
  const renewServiceMutation = useMutation(renewWhatsappService, {
    onSuccess: () => {
      // Invalidar queries relacionadas para refrescar datos
      queryClient.invalidateQueries(["whatsapp-qr-status"]);
      queryClient.invalidateQueries(["whatsapp-service-status"]);
      queryClient.invalidateQueries(["whatsapp-is-online"]);
    },
  });

  // Mutation para forzar nueva sesión
  const forceNewSessionMutation = useMutation(forceNewWhatsappSession, {
    onSuccess: () => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries(["whatsapp-qr-status"]);
      queryClient.invalidateQueries(["whatsapp-service-status"]);
      queryClient.invalidateQueries(["whatsapp-is-online"]);
    },
  });

  // Mutation para reiniciar servicio
  const restartServiceMutation = useMutation(restartWhatsappService, {
    onSuccess: () => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries(["whatsapp-qr-status"]);
      queryClient.invalidateQueries(["whatsapp-service-status"]);
      queryClient.invalidateQueries(["whatsapp-is-online"]);
    },
  });

  // Mutation para enviar mensaje
  const sendMessageMutation = useMutation(sendWhatsappMessage);

  return {
    // Queries
    qrStatusQuery,
    serviceStatusQuery,
    isOnlineQuery,
    debugFilesQuery,

    // Mutations
    renewServiceMutation,
    forceNewSessionMutation,
    restartServiceMutation,
    sendMessageMutation,

    // Métodos de utilidad
    refetchQrStatus: () =>
      queryClient.invalidateQueries(["whatsapp-qr-status"]),
    refetchServiceStatus: () =>
      queryClient.invalidateQueries(["whatsapp-service-status"]),
    refetchIsOnline: () =>
      queryClient.invalidateQueries(["whatsapp-is-online"]),
    refetchDebugFiles: () => debugFilesQuery.refetch(),
  };
};
